import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/api/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Camera, ScanLine, Sparkles, RefreshCw, CheckCircle2, Loader2,
  PackagePlus, PackageOpen, ChevronRight, UserCheck, PenTool,
} from "lucide-react";
import InstallAppButton from "@/components/InstallAppButton";

/**
 * Mobile-first scan page. Two big buttons: Inward / Outward. Opens the phone
 * camera directly, runs AI OCR, and drops you into the entry form pre-filled.
 */
export default function ScanPage() {
  const [direction, setDirection] = useState(null); // 'inward' | 'outward'
  const [imgUrl, setImgUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const fileRef = useRef(null);
  const nav = useNavigate();

  const openCamera = (dir) => {
    setDirection(dir);
    setImgUrl(null);
    setExtracted(null);
    // small delay for state, then click file input
    setTimeout(() => fileRef.current?.click(), 30);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setImgUrl(URL.createObjectURL(file));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ai/scan-label", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const x = data.extracted || {};
      setExtracted(x);
      if (x.photo_url) setImgUrl(fileUrl(x.photo_url));
      toast.success("Label read", { description: `${x.courier || "?"} · ${x.tracking_number || "no TN"}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI scan failed");
    } finally { setBusy(false); }
  };

  const proceed = () => {
    // Hand off extracted data via location state
    nav(`/app/${direction}`, { state: { extracted } });
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="max-w-xl mx-auto px-4 pt-6 md:pt-10 space-y-5">
        <header className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="overline flex items-center gap-2"><ScanLine size={12}/> Mobile scan</div>
            <h1 className="text-3xl md:text-4xl font-display mt-1">Scan a parcel.</h1>
            <p className="text-sm text-muted-foreground mt-1">Point at the label — printed or handwritten. AI reads sender, recipient, courier & tracking, then auto-matches the recipient in your employee directory.</p>
          </div>
          <InstallAppButton testid="scan-install-btn" compact />
        </header>

        {/* Direction picker */}
        {!direction && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              data-testid="scan-inward-cta"
              onClick={() => openCamera("inward")}
              className="rounded-md border border-border bg-card p-5 text-left card-elev hover-brut active:scale-[.98]"
            >
              <PackagePlus size={22} className="text-primary mb-3" />
              <div className="font-display text-lg">Inward</div>
              <div className="text-xs text-muted-foreground">Log an arrival</div>
            </button>
            <button
              data-testid="scan-outward-cta"
              onClick={() => openCamera("outward")}
              className="rounded-md border border-border bg-card p-5 text-left card-elev hover-brut active:scale-[.98]"
            >
              <PackageOpen size={22} className="text-accent mb-3" />
              <div className="font-display text-lg">Outward</div>
              <div className="text-xs text-muted-foreground">Book a shipment</div>
            </button>
            <button
              data-testid="scan-inward-log-cta"
              onClick={() => nav("/app/inward-log?new=1")}
              className="rounded-md border-2 border-primary/40 bg-red-50/40 p-5 text-left card-elev hover-brut active:scale-[.98]"
            >
              <ScanLine size={22} className="text-primary mb-3" />
              <div className="font-display text-lg">Inward Log Scan</div>
              <div className="text-xs text-muted-foreground">Scan &amp; log a batch arrival</div>
            </button>
          </div>
        )}

        {/* Scan surface */}
        {direction && (
          <div className="rounded-md border border-border bg-card overflow-hidden card-elev">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                {direction === "inward" ? <PackagePlus size={14} className="text-primary" /> : <PackageOpen size={14} className="text-accent" />}
                <div className="font-semibold text-sm">{direction === "inward" ? "Inward scan" : "Outward scan"}</div>
              </div>
              <button data-testid="scan-reset" onClick={() => { setDirection(null); setImgUrl(null); setExtracted(null); }}
                className="text-xs mono uppercase tracking-widest text-muted-foreground hover:text-foreground">Change</button>
            </div>

            <div className="aspect-[4/5] bg-muted/40 scan-frame flex items-center justify-center relative" data-testid="scan-mobile-preview">
              {imgUrl ? (
                <img src={imgUrl} alt="label" className="w-full h-full object-contain" />
              ) : (
                <div className="text-center px-6">
                  <Camera size={40} className="mx-auto text-muted-foreground" />
                  <div className="mt-3 text-sm">Tap below to open camera.</div>
                </div>
              )}
              {busy && <div className="scan-beam" />}
              {busy && (
                <div className="absolute inset-0 bg-foreground/10 flex items-end justify-center pb-4">
                  <div className="mono text-xs bg-foreground text-background px-3 py-1 uppercase tracking-widest flex items-center gap-2">
                    <Loader2 size={12} className="animate-spin" /> AI reading label…
                  </div>
                </div>
              )}
            </div>

            {/* Extracted preview */}
            {extracted && (
              <div className="p-4 border-t border-border space-y-3 text-sm">
                <div className="flex items-center gap-2 text-primary">
                  <CheckCircle2 size={14} />
                  <span className="overline text-primary/80">Extracted fields</span>
                  {extracted.handwritten && (
                    <span className="text-[9px] font-bold uppercase tracking-widest bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded flex items-center gap-1" data-testid="handwritten-badge">
                      <PenTool size={9} /> Handwritten detected
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-y-1 gap-x-3">
                  <Kv k="Tracking" v={extracted.tracking_number} mono />
                  <Kv k="Courier" v={extracted.courier} />
                  <Kv k="Sender" v={extracted.sender_name} />
                  <Kv k="Recipient" v={extracted.recipient_name} />
                  <Kv k="Weight" v={extracted.weight_kg ? `${extracted.weight_kg} kg` : "—"} />
                  <Kv k="Type" v={extracted.parcel_type} />
                </div>

                {extracted.matched_recipient && (
                  <div className="border-2 border-primary rounded-md bg-red-50 p-3" data-testid="matched-recipient-card">
                    <div className="flex items-center gap-2 mb-2">
                      <UserCheck size={16} className="text-primary" />
                      <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Auto-matched employee</div>
                    </div>
                    <div className="font-semibold text-base">{extracted.matched_recipient.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {extracted.matched_recipient.department || "—"} · {extracted.matched_recipient.email || extracted.matched_recipient.phone || "no contact"}
                    </div>
                    <div className="text-[10px] mono uppercase tracking-widest text-primary mt-1">
                      Confidence {(extracted.matched_recipient.score * 100).toFixed(0)}%
                    </div>
                  </div>
                )}
                {!extracted.matched_recipient && (extracted.match_candidates || []).length > 0 && (
                  <div className="border border-amber-300 bg-amber-50 rounded-md p-3" data-testid="match-candidates">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-amber-900 mb-2">
                      Possible employees (confirm on next screen)
                    </div>
                    <div className="space-y-1">
                      {extracted.match_candidates.map((c) => (
                        <div key={c.id} className="text-xs flex items-center justify-between">
                          <span><b>{c.name}</b> <span className="text-muted-foreground">· {c.department || "—"}</span></span>
                          <span className="mono text-[10px] text-amber-800">{(c.score * 100).toFixed(0)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="p-4 border-t border-border grid grid-cols-2 gap-2">
              <Button
                data-testid="scan-open-camera"
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="rounded-md bg-primary text-primary-foreground h-12 text-base shadow-brut-orange"
              >
                <Camera size={16} className="mr-2" /> {imgUrl ? "Retake" : "Open camera"}
              </Button>
              <Button
                data-testid="scan-continue"
                onClick={proceed}
                disabled={busy || !extracted}
                variant="outline"
                className="rounded-md h-12 text-base"
              >
                Continue <ChevronRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        <div className="rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground flex items-start gap-3">
          <Sparkles size={16} className="text-accent mt-0.5 shrink-0" />
          <div>
            <b className="text-foreground">Hybrid entry:</b> AI reads printed and handwritten labels. Any field the AI missed
            appears as a normal input on the next screen — you can fix, add, or reorder before saving.
          </div>
        </div>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        data-testid="scan-mobile-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
}

const Kv = ({ k, v, mono }) => (
  <>
    <div className="text-muted-foreground text-xs">{k}</div>
    <div className={`text-right ${mono ? "mono text-xs" : "text-sm"}`}>{v || "—"}</div>
  </>
);
